import React, { useState, useRef } from 'react';
import { Bold, Italic, Underline, Type, Palette, Scissors, Copy, Clipboard, Paintbrush } from 'lucide-react';

const RichTextToolbar = ({ onFormat, targetRef }) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showFontPicker, setShowFontPicker] = useState(false);
  const [copiedFormat, setCopiedFormat] = useState(null);

  const fonts = [
    'Arial',
    'Times New Roman',
    'Courier New',
    'Georgia',
    'Verdana',
    'Helvetica',
    'Tahoma',
    'Trebuchet MS'
  ];

  const fontSizes = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px'];

  const colors = [
    '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF',
    '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#800080',
    '#008000', '#800000', '#808080', '#C0C0C0', '#FFD700'
  ];

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (onFormat) onFormat();
  };

  const handleCut = () => {
    document.execCommand('cut');
    if (onFormat) onFormat();
  };

  const handleCopy = () => {
    document.execCommand('copy');
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      document.execCommand('insertText', false, text);
      if (onFormat) onFormat();
    } catch (err) {
      // Fallback to execCommand paste
      document.execCommand('paste');
      if (onFormat) onFormat();
    }
  };

  const copyFormat = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const element = range.startContainer.parentElement;
      
      if (element) {
        const computedStyle = window.getComputedStyle(element);
        setCopiedFormat({
          fontFamily: computedStyle.fontFamily,
          fontSize: computedStyle.fontSize,
          fontWeight: computedStyle.fontWeight,
          fontStyle: computedStyle.fontStyle,
          textDecoration: computedStyle.textDecoration,
          color: computedStyle.color,
          textAlign: computedStyle.textAlign
        });
      }
    }
  };

  const pasteFormat = () => {
    if (!copiedFormat) return;
    
    // Apply the copied format
    if (copiedFormat.fontFamily) execCommand('fontName', copiedFormat.fontFamily);
    if (copiedFormat.fontSize) {
      // Convert px to font size number (1-7)
      const size = parseInt(copiedFormat.fontSize);
      let fontSizeNum = 3; // default
      if (size <= 10) fontSizeNum = 1;
      else if (size <= 13) fontSizeNum = 2;
      else if (size <= 16) fontSizeNum = 3;
      else if (size <= 18) fontSizeNum = 4;
      else if (size <= 24) fontSizeNum = 5;
      else if (size <= 36) fontSizeNum = 6;
      else fontSizeNum = 7;
      execCommand('fontSize', fontSizeNum);
    }
    if (copiedFormat.fontWeight === 'bold' || parseInt(copiedFormat.fontWeight) >= 600) {
      execCommand('bold');
    }
    if (copiedFormat.fontStyle === 'italic') {
      execCommand('italic');
    }
    if (copiedFormat.textDecoration.includes('underline')) {
      execCommand('underline');
    }
    if (copiedFormat.color) {
      execCommand('foreColor', copiedFormat.color);
    }
    if (copiedFormat.textAlign) {
      if (copiedFormat.textAlign === 'left') execCommand('justifyLeft');
      else if (copiedFormat.textAlign === 'center') execCommand('justifyCenter');
      else if (copiedFormat.textAlign === 'right') execCommand('justifyRight');
    }
  };

  return (
    <div className="bg-gray-100 border-b-2 border-gray-300 p-3 flex flex-wrap items-center gap-2 sticky top-0 z-10 shadow-md">
      {/* Cut/Copy/Paste */}
      <button
        onClick={handleCut}
        className="p-2 bg-white border-2 border-gray-300 rounded-lg hover:bg-yellow-100 active:bg-yellow-200"
        title="Cut (Ctrl+X)"
      >
        <Scissors className="w-5 h-5" />
      </button>
      <button
        onClick={handleCopy}
        className="p-2 bg-white border-2 border-gray-300 rounded-lg hover:bg-blue-100 active:bg-blue-200"
        title="Copy (Ctrl+C)"
      >
        <Copy className="w-5 h-5" />
      </button>
      <button
        onClick={handlePaste}
        className="p-2 bg-white border-2 border-gray-300 rounded-lg hover:bg-green-100 active:bg-green-200"
        title="Paste (Ctrl+V)"
      >
        <Clipboard className="w-5 h-5" />
      </button>

      <div className="w-px h-8 bg-gray-300"></div>

      {/* Format Painter */}
      <button
        onClick={copyFormat}
        className={`p-2 border-2 rounded-lg hover:bg-purple-100 active:bg-purple-200 ${
          copiedFormat ? 'bg-purple-200 border-purple-400' : 'bg-white border-gray-300'
        }`}
        title="Copy Format"
      >
        <Paintbrush className="w-5 h-5" />
      </button>
      <button
        onClick={pasteFormat}
        className="p-2 bg-white border-2 border-gray-300 rounded-lg hover:bg-purple-100 active:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={!copiedFormat}
        title="Apply Format"
      >
        <Paintbrush className="w-5 h-5 text-purple-600" />
      </button>

      <div className="w-px h-8 bg-gray-300"></div>

      {/* Font Family */}
      <div className="relative">
        <button
          onClick={() => setShowFontPicker(!showFontPicker)}
          className="px-4 py-2 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 font-semibold"
        >
          <Type className="w-4 h-4" />
          Font
        </button>
        {showFontPicker && (
          <div className="absolute top-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-xl z-20 max-h-60 overflow-y-auto">
            {fonts.map((font) => (
              <button
                key={font}
                onClick={() => {
                  execCommand('fontName', font);
                  setShowFontPicker(false);
                }}
                className="block w-full px-4 py-2 text-left hover:bg-indigo-50 whitespace-nowrap"
                style={{ fontFamily: font }}
              >
                {font}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Font Size */}
      <select
        onChange={(e) => execCommand('fontSize', e.target.value)}
        className="px-3 py-2 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-semibold cursor-pointer"
        defaultValue="3"
      >
        <option value="1">8pt</option>
        <option value="2">10pt</option>
        <option value="3">12pt</option>
        <option value="4">14pt</option>
        <option value="5">18pt</option>
        <option value="6">24pt</option>
        <option value="7">36pt</option>
      </select>

      <div className="w-px h-8 bg-gray-300"></div>

      {/* Bold */}
      <button
        onClick={() => execCommand('bold')}
        className="p-2 bg-white border-2 border-gray-300 rounded-lg hover:bg-indigo-100 active:bg-indigo-200"
        title="Bold (Ctrl+B)"
      >
        <Bold className="w-5 h-5" />
      </button>

      {/* Italic */}
      <button
        onClick={() => execCommand('italic')}
        className="p-2 bg-white border-2 border-gray-300 rounded-lg hover:bg-indigo-100 active:bg-indigo-200"
        title="Italic (Ctrl+I)"
      >
        <Italic className="w-5 h-5" />
      </button>

      {/* Underline */}
      <button
        onClick={() => execCommand('underline')}
        className="p-2 bg-white border-2 border-gray-300 rounded-lg hover:bg-indigo-100 active:bg-indigo-200"
        title="Underline (Ctrl+U)"
      >
        <Underline className="w-5 h-5" />
      </button>

      <div className="w-px h-8 bg-gray-300"></div>

      {/* Text Color */}
      <div className="relative">
        <button
          onClick={() => setShowColorPicker(!showColorPicker)}
          className="px-4 py-2 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          title="Text Color"
        >
          <Palette className="w-5 h-5" />
          <span className="font-semibold">Color</span>
        </button>
        {showColorPicker && (
          <div className="absolute top-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-xl p-3 z-20">
            <div className="grid grid-cols-5 gap-2">
              {colors.map((color) => (
                <button
                  key={color}
                  onClick={() => {
                    execCommand('foreColor', color);
                    setShowColorPicker(false);
                  }}
                  className="w-8 h-8 rounded border-2 border-gray-300 hover:border-indigo-500 transition-all"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="w-px h-8 bg-gray-300"></div>

      {/* Alignment */}
      <button
        onClick={() => execCommand('justifyLeft')}
        className="px-3 py-2 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-bold"
        title="Align Left"
      >
        ≡
      </button>
      <button
        onClick={() => execCommand('justifyCenter')}
        className="px-3 py-2 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-bold"
        title="Align Center"
      >
        ≡
      </button>
      <button
        onClick={() => execCommand('justifyRight')}
        className="px-3 py-2 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-bold"
        title="Align Right"
      >
        ≡
      </button>

      <div className="w-px h-8 bg-gray-300"></div>

      {/* Lists */}
      <button
        onClick={() => execCommand('insertUnorderedList')}
        className="px-3 py-2 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-bold"
        title="Bullet List"
      >
        •••
      </button>
      <button
        onClick={() => execCommand('insertOrderedList')}
        className="px-3 py-2 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-bold"
        title="Numbered List"
      >
        123
      </button>

      <div className="w-px h-8 bg-gray-300"></div>

      {/* Clear Formatting */}
      <button
        onClick={() => execCommand('removeFormat')}
        className="px-4 py-2 bg-red-100 border-2 border-red-300 rounded-lg hover:bg-red-200 font-semibold text-red-700"
        title="Clear Formatting"
      >
        Clear
      </button>
    </div>
  );
};

export default RichTextToolbar;
